import React from 'react';
import { motion } from 'framer-motion';
import { Building2, CheckCircle, ArrowRight, Mail, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const MunicipalIndustryPage: React.FC = () => {
  const systems = [
    {
      title: 'Inline Pressure Filters',
      description: 'Pre-treatment filtration before sand, membrane, or UV systems',
      link: '/filtration/inline/big'
    },
    {
      title: 'Self-Cleaning Suction Screens',
      description: 'Surface-water intake protection with automated cleaning',
      link: '/filtration/suction-screens/self-cleaning'
    },
    {
      title: 'Passive Fish Screens',
      description: 'NOAA/WDFW compliant screens for raw-water intakes',
      link: '/filtration/suction-screens/passive-fish-screens'
    }
  ];

  const benefits = [
    'Protect pumps, seals, membranes, and UV systems from debris damage',
    'Options meeting NOAA/WDFW fish-screen approach velocity and open-area criteria',
    'Built to handle variable flow and debris loading in surface water',
    'Stainless-steel construction for long service life in chlorinated environments',
    'Low maintenance with automated or semi-automated cleaning options',
    'NSF-61 certified materials available for potable water contact'
  ];

  const applications = [
    {
      title: 'Raw-Water Intake Protection',
      description: 'Protect pumps at the source with self-cleaning or passive fish-friendly screens for rivers, lakes, and canals.'
    },
    {
      title: 'Pre-Treatment Filtration',
      description: 'Remove coarse solids before sand filters, membrane systems, or UV disinfection to extend equipment life.'
    },
    {
      title: 'Lift-Station and Pump-Station Screening',
      description: 'Prevent debris from damaging pumps and downstream equipment in wastewater and stormwater systems.'
    },
    {
      title: 'Reclaimed-Water Discharge',
      description: 'Polish reclaimed water before discharge or reuse with automated inline filtration.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Municipal Water & Wastewater Filtration | CTC Waterworks"
        description="Reliable stainless-steel filtration for municipal raw-water intakes, pre-treatment, pump stations, and reclaimed water. Options meeting NOAA/WDFW fish-screen criteria."
        canonical="https://www.ctcwaterworks.com/industries/municipal"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                    <Building2 className="text-white" size={32} />
                  </div>
                  <h1 className="text-5xl font-bold">
                    Municipal Water & Wastewater
                  </h1>
                </div>
                <p className="text-2xl text-blue-100 mb-6 font-light leading-relaxed">
                  From surface-water intakes to pump-station pre-filters, our stainless-steel systems provide reliable solids removal for municipalities large and small.
                </p>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
                  <div className="flex items-start">
                    <Shield className="mr-3 text-blue-200 mt-1 flex-shrink-0" size={24} />
                    <p className="text-blue-100 leading-relaxed">
                      Options meeting NOAA/WDFW fish-screen approach velocity and open-area criteria for raw-water intakes
                    </p>
                  </div>
                </div>
              </motion.div>

              <div className="bg-white rounded-lg shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Get Expert Help</h3>
                <Link
                  to="/contact?topic=municipal-filtration"
                  className="block w-full bg-blue-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center shadow-lg mb-4"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>
                <p className="text-sm text-gray-600 text-center">
                  Our team will help you design the right filtration system for your municipal water application
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Why CTC Filtration for Municipal Systems?
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Built for the reliability and compliance demands of municipal water systems
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <CheckCircle className="mr-3 text-blue-600 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700 leading-relaxed">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Common Applications */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Common Applications
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              CTC filtration systems protect municipal water infrastructure
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {applications.map((app, index) => (
                <div key={index} className="bg-white border-2 border-blue-200 rounded-lg p-8 hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{app.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Systems Overview */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Recommended Systems
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Choose from inline filters or intake screens based on your application
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {systems.map((system, index) => (
                <div key={index} className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-6 hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{system.title}</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">{system.description}</p>
                  <Link
                    to={system.link}
                    className="flex items-center justify-between bg-white border-2 border-blue-600 text-blue-600 px-4 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors"
                  >
                    <span>Learn More</span>
                    <ArrowRight size={20} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Highlight */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white border-2 border-blue-300 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Fish-Screen Compliance for Raw-Water Intakes
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Our Passive Fish Screens (PSF) meet NOAA/WDFW approach-velocity and open-area criteria, making them
                approved for surface-water and dam intake use. Wedgewire construction maintains less than 0.2 ft/sec approach
                velocity to protect juvenile fish while providing reliable intake protection for municipal water systems.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">&lt;0.2 ft/sec</p>
                  <p className="text-sm text-gray-600">Approach Velocity</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">NOAA/WDFW</p>
                  <p className="text-sm text-gray-600">Compliant</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">Wedgewire</p>
                  <p className="text-sm text-gray-600">Construction</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Municipal Water System?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Our engineering team will help you select the right filtration system for your raw-water source, treatment process, and compliance requirements.
            </p>
            <Link
              to="/contact?topic=municipal-filtration"
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Talk to a Filtration Expert
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default MunicipalIndustryPage;
